// After a deploy, an open tab still asks for the old lazy chunks, which no
// longer exist. Reloading once picks up the new build; the guard in
// sessionStorage keeps a broken deploy from reloading in a loop.
export const CHUNK_RELOAD_GUARD_KEY = 'chunk-load-auto-reload-at';
const CHUNK_RELOAD_GUARD_WINDOW_MS = 60 * 1000;

type ReloadEnvironment = {
  storage: () => Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
};

export const isChunkLoadError = (error: any) => {
  const message = String(error?.message || '');

  return (
    error != null &&
    (error.name === 'ChunkLoadError' ||
      /loading( css)? chunk .* failed/i.test(message) ||
      (error.name === 'SyntaxError' && /Unexpected token\s*(?:'|")?</i.test(message)))
  );
};

export const createReloadOnChunkFailure = ({
  storage,
  reload,
}: ReloadEnvironment) => {
  let autoReloadTriggered = false;

  const markAutoReload = () => {
    try {
      const lastReloadAt = Number(storage().getItem(CHUNK_RELOAD_GUARD_KEY));
      const sinceLastReload = Date.now() - lastReloadAt;
      if (sinceLastReload >= 0 && sinceLastReload < CHUNK_RELOAD_GUARD_WINDOW_MS) {
        return false;
      }
      storage().setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
      return true;
    } catch {
      return false;
    }
  };

  return <T>(factory: () => Promise<T>) => (): Promise<T> =>
    factory().catch((error: any) => {
      if (!isChunkLoadError(error)) throw error;
      if (!autoReloadTriggered) {
        if (!markAutoReload()) throw error;
        autoReloadTriggered = true;
        reload();
      }
      return new Promise<never>(() => {});
    });
};

export const withReloadOnChunkFailure = createReloadOnChunkFailure({
  storage: () => window.sessionStorage,
  reload: () => window.location.reload(),
});
